// /sports/super-admin/devices — the fleet, managed by the MACHINE OWNER (super
// admin): every rental kiosk phone, online or not, in a session or not, rows
// waiting. Pair, rename, set site, link to a tenant, the court's permanent
// public link, the staff-PIN status, revoke, re-pair; the owner code (TOTP)
// the phone's owner-gated actions check against.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Smartphone, Plus, KeyRound, RefreshCw, Copy, RotateCcw, Pencil, MapPin, Ban, Link2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import ConfirmModal from '../components/ConfirmModal';
import { SuperAdminShell } from './SportsSuperAdminPage';
import { listDevices, listTenants, updateDevice, revokeDevice, rotateCourtLink, getTotpSetup, rotateTotp, getKioskAlerts, setKioskAlerts, isAdminDenied, errMsg, errDetail, getOwnerPin, setOwnerPin } from '../kiosk/kioskApi';
import {
  RED, GREEN, GREY, AMBER, OnlineDot, StateBadge, PairDeviceDialog, TextPromptModal, TenantSelect, CourtLinkCell, StaffPinStatus,
  AdminDeniedNotice, SyncBadge, fmtMs, fmtAgo, labelStyle, iconBtn, copyText,
} from '../kiosk/KioskDialogs';
import { liveLine } from '../kiosk/KioskLive';
import { useAutoRefresh } from '../kiosk/useAutoRefresh';


// `to` (optional, K8 P2.5 P4): a tile that is also a shortcut to the page where
// that number is explained — the "In use" tile opens the live board.
function StatTile({ label, value, color, testId, to = null }) {
  const body = (
    <div style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: '14px 16px', height: '100%' }}>
      <p style={{ fontSize: 11, color: GREY, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}{to && <span style={{ color: '#555' }}> →</span>}</p>
      <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 26, fontWeight: 900, color }} data-testid={testId}>{value}</p>
    </div>
  );
  return to
    ? <Link to={to} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }} data-testid={`${testId}-link`} title="Open the live board">{body}</Link>
    : body;
}

// K8 P2: who gets the email when a phone reports an emergency, a machine
// problem, a dead phone or a late stop. The phone tells the staff "reported to
// HighMark"; this is the list that makes it true.
function AlertsPanel({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [emails, setEmails] = useState('');
  const [kinds, setKinds] = useState([]);
  const [activity, setActivity] = useState([]);
  const [notifyCourt, setNotifyCourt] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    getKioskAlerts().then(c => { setCfg(c); setEmails((c.emails || []).join(', ')); setKinds(c.kinds || []); setActivity(c.activity_kinds || []); setNotifyCourt(c.notify_court !== false); })
      .catch(e => setError(errMsg(e, 'Could not load the alert settings')));
  }, []);
  const toggle = (k) => setKinds(v => (v.includes(k) ? v.filter(x => x !== k) : [...v, k]));
  const toggleActivity = (k) => setActivity(v => (v.includes(k) ? v.filter(x => x !== k) : [...v, k]));
  const save = async () => {
    setSaving(true);
    try {
      const r = await setKioskAlerts({ emails: emails.split(/[,;\s]+/).filter(Boolean), kinds, notify_court: notifyCourt, activity_kinds: activity });
      setCfg(r); setEmails((r.emails || []).join(', ')); setKinds(r.kinds || []); setActivity(r.activity_kinds || []); setNotifyCourt(r.notify_court !== false);
      toast.success(r.emails.length ? `Alerts go to ${r.emails.join(', ')}` : 'Alerts switched off — no recipients');
    } catch (e) { toast.error(errMsg(e, 'Could not save')); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="kiosk-alerts-panel" style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: 20, marginBottom: 24 }}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>Email alerts</h2>
          <p style={{ fontSize: 12, color: GREY, marginTop: 2, maxWidth: 560, lineHeight: 1.5 }}>
            Sent the moment a phone reports one of these stops, once per rental, with the staff&apos;s note and a link to the device page.
            The phone tells the staff the stop was reported to HighMark, so keep at least one address here.
          </p>
        </div>
        <button onClick={onClose} data-testid="kiosk-alerts-close" style={{ ...iconBtn, padding: '7px 12px', fontSize: 12, fontWeight: 700 }}>Close</button>
      </div>
      {error ? <p style={{ color: '#FF2D55', fontSize: 13, marginTop: 16 }}>{error}</p> : !cfg ? <p style={{ color: GREY, fontSize: 13, marginTop: 16 }}>Loading…</p> : (
        <div style={{ marginTop: 16 }}>
          {!cfg.email_configured && <p style={{ fontSize: 12, color: AMBER, marginBottom: 10 }}>The server has no email key configured, so nothing can be sent until it does.</p>}
          <p style={labelStyle}>Send to (comma-separated, up to 5)</p>
          <input value={emails} onChange={e => setEmails(e.target.value)} data-testid="kiosk-alerts-emails" placeholder={cfg.default_email || 'owner@example.com'}
            style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8, padding: '10px 12px', width: '100%', maxWidth: 520, outline: 'none', fontSize: 14, marginTop: 6 }} />
          {!cfg.stored && cfg.default_email && <p style={{ fontSize: 11, color: '#555', marginTop: 4 }}>Nothing saved yet: alerts currently go to the platform admin, {cfg.default_email}.</p>}
          <p style={{ ...labelStyle, marginTop: 14 }}>Email me for</p>
          <div className="flex gap-2 flex-wrap mt-2">
            {(cfg.kinds_available || []).map(k => {
              const on = kinds.includes(k);
              return (
                <button key={k} onClick={() => toggle(k)} data-testid={`kiosk-alerts-kind-${k}`}
                  style={{ fontSize: 12, fontWeight: 700, padding: '7px 14px', borderRadius: 999, cursor: 'pointer',
                    border: `1px solid ${on ? GREEN : '#2A2A2A'}`, background: on ? GREEN + '18' : 'transparent', color: on ? GREEN : GREY }}>
                  {cfg.kind_labels?.[k] || k}
                </button>
              );
            })}
          </div>
          <p style={{ ...labelStyle, marginTop: 14 }}>Notify my phone (HighMark Manage) for</p>
          <p style={{ fontSize: 11, color: '#555', marginTop: 2 }}>The ordinary day: push only, no email, no court copy, once per rental.</p>
          <div className="flex gap-2 flex-wrap mt-2">
            {(cfg.activity_available || []).map(k => {
              const on = activity.includes(k);
              return (
                <button key={k} onClick={() => toggleActivity(k)} data-testid={`kiosk-alerts-activity-${k}`}
                  style={{ fontSize: 12, fontWeight: 700, padding: '7px 14px', borderRadius: 999, cursor: 'pointer',
                    border: `1px solid ${on ? GREEN : '#2A2A2A'}`, background: on ? GREEN + '18' : 'transparent', color: on ? GREEN : GREY }}>
                  {cfg.activity_labels?.[k] || k}
                </button>
              );
            })}
          </div>
          <label className="flex items-center gap-2 mt-4" style={{ fontSize: 13, color: '#ccc', cursor: 'pointer' }} data-testid="kiosk-alerts-court">
            <input type="checkbox" checked={notifyCourt} onChange={e => setNotifyCourt(e.target.checked)} />
            Also email the linked court&apos;s registered owner a court-addressed copy: same facts, told that HighMark has it on record and will call if needed.
          </label>
          <div className="flex gap-2 mt-4">
            <button onClick={save} disabled={saving} data-testid="kiosk-alerts-save"
              style={{ background: RED, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5, cursor: 'pointer' }}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function OwnerCodePanel({ onClose }) {
  const [setup, setSetup] = useState(null);
  const [error, setError] = useState('');
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [pinInfo, setPinInfo] = useState(null);           // 7.0e: the fleet owner PIN
  const [newPin, setNewPin] = useState('');
  const [newPin2, setNewPin2] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [savingPin, setSavingPin] = useState(false);

  const load = useCallback(() => {
    setError('');
    getTotpSetup().then(setSetup).catch(e => setError(errMsg(e, 'Could not load the owner code')));
    getOwnerPin().then(setPinInfo).catch(() => setPinInfo(null));
  }, []);
  const savePin = async () => {
    setSavingPin(true);
    try {
      const r = await setOwnerPin({ pin: newPin, current_pin: pinCode });
      toast.success(`Owner PIN ${r.changed ? 'changed' : 'set'} — v${r.version}. Phones take it on their next beat.`);
      setNewPin(''); setNewPin2(''); setPinCode(''); setPinInfo(r);
    } catch (e) { const d = errDetail(e); toast.error((d && d.message) || errMsg(e, 'Could not save the PIN')); }
    finally { setSavingPin(false); }
  };
  useEffect(() => { load(); }, [load]);

  const rotate = async () => {
    setConfirmRotate(false);
    setRotating(true);
    try {
      const r = await rotateTotp();
      toast.success(`Owner code rotated — now version ${r.version}. Scan the new QR.`);
      load();
    } catch (e) { toast.error(errMsg(e, 'Rotate failed')); }
    finally { setRotating(false); }
  };

  return (
    <div data-testid="kiosk-owner-code-panel" style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, padding: 20, marginBottom: 24 }}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>Owner code</h2>
          <p style={{ fontSize: 12, color: GREY, marginTop: 2, maxWidth: 520, lineHeight: 1.5 }}>
            One code for the whole fleet. Scan it once into an authenticator app (Google Authenticator, Aegis). The 6-digit code it shows is
            what every phone asks for on owner actions. Paired phones pick up a rotation on their next heartbeat.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setConfirmRotate(true)} disabled={!setup || rotating} data-testid="kiosk-totp-rotate"
            style={{ ...iconBtn, padding: '7px 12px', fontSize: 12, fontWeight: 700, color: AMBER, borderColor: AMBER + '40' }}>
            <RotateCcw className="h-4 w-4" /> {rotating ? 'Rotating…' : 'Rotate'}
          </button>
          <button onClick={onClose} data-testid="kiosk-owner-code-close" style={{ ...iconBtn, padding: '7px 12px', fontSize: 12, fontWeight: 700 }}>Close</button>
        </div>
      </div>

      {error ? (
        <p style={{ color: '#FF2D55', fontSize: 13, marginTop: 16 }}>{error}</p>
      ) : !setup ? (
        <p style={{ color: GREY, fontSize: 13, marginTop: 16 }}>Loading…</p>
      ) : (
        <div className="flex gap-6 mt-5 flex-wrap items-start">
          <div style={{ background: '#fff', padding: 12, borderRadius: 12 }} data-testid="kiosk-totp-qr">
            <QRCodeSVG value={setup.otpauth_uri} size={176} level="M" />
          </div>
          <div style={{ flex: 1, minWidth: 260 }}>
            <p style={labelStyle}>Version</p>
            <p data-testid="kiosk-totp-version" style={{ fontFamily: 'Oswald, sans-serif', fontSize: 22, fontWeight: 800, color: '#fff' }}>v{setup.version}</p>
            <p style={{ ...labelStyle, marginTop: 12 }}>Secret (if you cannot scan)</p>
            <div className="flex items-center gap-2 mt-1">
              <code data-testid="kiosk-totp-secret" style={{ fontSize: 13, color: '#fff', background: '#0A0A0A', border: '1px solid #2A2A2A', borderRadius: 8, padding: '8px 10px', letterSpacing: 1, wordBreak: 'break-all' }}>
                {setup.secret}
              </code>
              <button onClick={() => copyText(setup.secret, 'the secret')} data-testid="kiosk-totp-copy-secret" style={iconBtn} title="Copy secret"><Copy className="h-4 w-4" /></button>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <span style={{ fontSize: 11, color: '#555', wordBreak: 'break-all', flex: 1 }}>{setup.otpauth_uri}</span>
              <button onClick={() => copyText(setup.otpauth_uri, 'the setup link')} data-testid="kiosk-totp-copy-uri" style={iconBtn} title="Copy otpauth link"><Copy className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 22, paddingTop: 18, borderTop: '1px solid #1E1E1E' }} data-testid="kiosk-owner-pin">
        <h2 style={{ fontFamily: 'Oswald, sans-serif', fontSize: 16, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>Owner PIN</h2>
        <p style={{ fontSize: 12, color: GREY, marginTop: 2, maxWidth: 560, lineHeight: 1.5 }}>
          One quick PIN for every phone, set here or in the Management App and delivered on their next beat; the PIN never travels, only a salted hash.
          Phones open owner actions with it; the web and the app accept it to collect. Changing it asks for the current PIN (your authenticator code works if you forgot it).
        </p>
        <p style={{ fontSize: 13, color: '#fff', marginTop: 10 }} data-testid="kiosk-owner-pin-status">
          {!pinInfo ? 'Loading…' : !pinInfo.set ? 'Not set yet — each phone keeps the PIN typed at its setup.'
            : `Set · v${pinInfo.version}${pinInfo.updated_by ? ` by ${pinInfo.updated_by}` : ''} · ${pinInfo.phones_behind > 0 ? `${pinInfo.phones_behind} phone(s) still on the old one` : 'all phones have it'}`}
        </p>
        <div className="flex gap-2 flex-wrap mt-3 items-center">
          <input value={newPin} onChange={e => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))} data-testid="kiosk-owner-pin-new" inputMode="numeric" type="password" placeholder="New 6-digit PIN"
            style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8, padding: '8px 12px', width: 170, outline: 'none', fontSize: 14, letterSpacing: 2 }} />
          <input value={newPin2} onChange={e => setNewPin2(e.target.value.replace(/\D/g, '').slice(0, 6))} data-testid="kiosk-owner-pin-repeat" inputMode="numeric" type="password" placeholder="Repeat"
            style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8, padding: '8px 12px', width: 120, outline: 'none', fontSize: 14, letterSpacing: 2 }} />
          {pinInfo?.set && <input value={pinCode} onChange={e => setPinCode(e.target.value.replace(/\D/g, '').slice(0, 6))} data-testid="kiosk-owner-pin-code" inputMode="numeric" type="password" placeholder="Current PIN"
            style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8, padding: '8px 12px', width: 170, outline: 'none', fontSize: 14, letterSpacing: 2 }} />}
          <button onClick={savePin} disabled={savingPin || newPin.length !== 6 || newPin2 !== newPin || (pinInfo?.set && pinCode.length !== 6)} data-testid="kiosk-owner-pin-save"
            style={{ background: RED, color: '#fff', border: 'none', borderRadius: 10, padding: '9px 16px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5, cursor: 'pointer' }}>
            {savingPin ? 'Saving…' : pinInfo?.set ? 'Change PIN' : 'Set PIN'}
          </button>
        </div>
      </div>

      {confirmRotate && (
        <ConfirmModal title="Rotate the owner code?" color={AMBER} confirmLabel="Rotate"
          message="Your authenticator app will stop matching until you scan the new QR. Phones switch to the new code on their next heartbeat (within about 2 minutes while online)."
          onConfirm={rotate} onCancel={() => setConfirmRotate(false)} />
      )}
    </div>
  );
}

export default function SportsKioskFleetPage() {
  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [pairOpen, setPairOpen] = useState(false);
  const [rePair, setRePair] = useState(null);
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [prompt, setPrompt] = useState(null);      // { device, field }
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [rotateTarget, setRotateTarget] = useState(null);
  const [linking, setLinking] = useState('');      // kiosk_id being (un)linked

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    return listDevices()
      .then(d => { setRows(Array.isArray(d) ? d : []); setDenied(false); })
      .catch(e => { if (isAdminDenied(e)) setDenied(true); else if (!silent) toast.error(errMsg(e, 'Could not load devices')); })
      .finally(() => { setNow(Date.now()); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    listTenants().then(t => setTenants(Array.isArray(t) ? t : [])).catch(() => {});
  }, []);
  useAutoRefresh(() => load(true));

  const active = rows.filter(d => d.status === 'active');
  const online = active.filter(d => d.is_online).length;
  const offline = active.length - online;
  const inSession = active.filter(d => d.session).length;
  const rowsWaiting = active.reduce((s, d) => s + (d.rows_pending || 0), 0);
  const pinMissing = active.filter(d => !d.staff_pin_present).length;
  const behind = active.filter(d => d.sync_state?.known && !d.sync_state.in_sync).length;
  const commandsPending = active.reduce((s, d) => s + (d.commands_pending || 0), 0);

  const saveField = async (device, field, value) => {
    await updateDevice(device.kiosk_id, { [field]: value });
    toast.success(field === 'name' ? 'Renamed' : 'Site updated');
    load(true);
  };

  const setTenant = async (device, orgId) => {
    setLinking(device.kiosk_id);
    try {
      const r = await updateDevice(device.kiosk_id, { court_org_id: orgId || null });
      toast.success(orgId ? `${device.name} linked to ${r.court_name || 'tenant'}` : `${device.name} unlinked — public link only`);
      load(true);
    } catch (e) { toast.error(errMsg(e, 'Could not change the tenant link')); }
    finally { setLinking(''); }
  };

  const doRevoke = async () => {
    const d = revokeTarget;
    setRevokeTarget(null);
    try { await revokeDevice(d.kiosk_id); toast.success(`${d.name} revoked — the phone will show "not paired"`); load(true); }
    catch (e) { toast.error(errMsg(e, 'Revoke failed')); }
  };

  const doRotate = async () => {
    const d = rotateTarget;
    setRotateTarget(null);
    try {
      const r = await rotateCourtLink(d.kiosk_id);
      toast.success(`New court link for ${d.name}: ${r.code}. The old link no longer opens.`);
      load(true);
    } catch (e) { toast.error(errMsg(e, 'Rotate failed')); }
  };

  const headerRight = (
    <div className="flex gap-2 flex-wrap">
      <button onClick={() => load()} data-testid="kiosk-refresh" title="Refresh" style={{ ...iconBtn, padding: '10px 12px' }}>
        <RefreshCw className="h-4 w-4" />
      </button>
      <button onClick={() => setAlertsOpen(v => !v)} data-testid="kiosk-alerts-btn" disabled={denied}
        style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', color: '#fff', border: '1px solid #2A2A2A',
          borderRadius: 10, padding: '11px 16px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5, cursor: 'pointer' }}>
        <Mail className="h-4 w-4" /> Alerts
      </button>
      <button onClick={() => setOwnerOpen(v => !v)} data-testid="kiosk-owner-code-btn" disabled={denied}
        style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', color: '#fff', border: '1px solid #2A2A2A',
          borderRadius: 10, padding: '11px 16px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5, cursor: 'pointer' }}>
        <KeyRound className="h-4 w-4" /> Owner code
      </button>
      <button onClick={() => setPairOpen(true)} data-testid="kiosk-pair-btn" disabled={denied}
        style={{ display: 'flex', alignItems: 'center', gap: 8, background: RED, color: '#fff', border: 'none',
          borderRadius: 10, padding: '11px 18px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase',
          fontFamily: 'Oswald, sans-serif', letterSpacing: 0.5, cursor: 'pointer', boxShadow: '0 0 20px rgba(230,57,70,0.4)' }}>
        <Plus className="h-4 w-4" /> Pair device
      </button>
    </div>
  );

  return (
    <SuperAdminShell title="Devices" active="devices" headerRight={headerRight}>
      <main style={{ padding: '20px 16px' }} data-testid="sports-kiosk-fleet-page">
        <p style={{ fontSize: 12, color: GREY, marginBottom: 16, maxWidth: 760, lineHeight: 1.5 }}>
          Rental kiosk phones — the phone is the record; this is what each one reports. Link a phone to a tenant so its rows land on their
          account, or leave it unlinked and hand the court its permanent link (opens with the phone&apos;s staff PIN).
        </p>

        {denied ? <AdminDeniedNotice /> : (
          <>
            {alertsOpen && <AlertsPanel onClose={() => setAlertsOpen(false)} />}
            {ownerOpen && <OwnerCodePanel onClose={() => setOwnerOpen(false)} />}

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
              <StatTile label="Online" value={online} color={GREEN} testId="kiosk-stat-online" />
              <StatTile label="Offline" value={offline} color={offline > 0 ? '#FF2D55' : '#fff'} testId="kiosk-stat-offline" />
              <StatTile label="In use" value={inSession} color={AMBER} testId="kiosk-stat-in-session" to="/sports/super-admin/live" />
              <StatTile label="Rows waiting" value={rowsWaiting} color={rowsWaiting > 0 ? AMBER : '#fff'} testId="kiosk-stat-rows-waiting" />
              <StatTile label="PIN not shared" value={pinMissing} color={pinMissing > 0 ? AMBER : '#fff'} testId="kiosk-stat-pin-missing" />
              <StatTile label="Out of sync" value={behind} color={behind > 0 ? AMBER : '#fff'} testId="kiosk-stat-behind" />
              <StatTile label="Commands pending" value={commandsPending} color={commandsPending > 0 ? AMBER : '#fff'} testId="kiosk-stat-commands-pending" />
            </div>

            {loading ? (
              <p style={{ color: GREY, textAlign: 'center', padding: 40 }}>Loading devices…</p>
            ) : rows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, background: '#141414', borderRadius: 12, border: '1px solid #1E1E1E' }}>
                <Smartphone className="h-10 w-10 mx-auto mb-3" style={{ color: '#333' }} />
                <p style={{ color: GREY, fontSize: 14 }}>No phones paired yet. Tap <b style={{ color: '#fff' }}>Pair device</b> and type the code the phone shows.</p>
              </div>
            ) : (
              <div style={{ background: '#141414', border: '1px solid #1E1E1E', borderRadius: 12, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1300 }} data-testid="kiosk-device-table">
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1E1E1E' }}>
                      {['', 'Device', 'Tenant', 'Court link', 'Staff PIN', 'In use', 'Rows', 'Sync', 'Commands', 'Last seen', ''].map((h, i) => (
                        <th key={i} style={{ ...labelStyle, textAlign: 'left', padding: '12px 14px', fontWeight: 700 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(d => (
                      <tr key={d.kiosk_id} data-testid={`kiosk-row-${d.kiosk_id}`} style={{ borderBottom: '1px solid #1E1E1E', opacity: d.status === 'revoked' ? 0.55 : 1 }}>
                        <td style={{ padding: '12px 0 12px 14px', width: 24 }}><OnlineDot device={d} /></td>
                        <td style={{ padding: '12px 14px' }}>
                          <Link to={`/sports/super-admin/devices/${d.kiosk_id}`} data-testid={`kiosk-open-${d.kiosk_id}`}
                            style={{ fontFamily: 'Oswald, sans-serif', fontSize: 15, fontWeight: 700, color: '#fff', textDecoration: 'none', letterSpacing: 0.3 }}>
                            {d.name}
                          </Link>
                          <div className="flex items-center gap-2 mt-1 flex-wrap"><StateBadge device={d} />
                            <span style={{ fontSize: 11, color: d.site_name ? GREY : '#555' }}>{d.site_name || 'no site'}</span>
                            {d.skew_flag && <span title={`Phone clock off by ${Math.round((d.skew_ms || 0) / 1000)} s`} style={{ fontSize: 10, color: AMBER, fontWeight: 700 }}>CLOCK</span>}
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          {d.status === 'active' ? (
                            <TenantSelect compact value={d.court_org_id} tenants={tenants} disabled={linking === d.kiosk_id}
                              onChange={v => setTenant(d, v)} testId={`kiosk-tenant-${d.kiosk_id}`} />
                          ) : (
                            <span style={{ fontSize: 12, color: d.court_name ? '#fff' : '#555' }}>{d.court_name || '—'}</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px' }}><CourtLinkCell device={d} onRotate={setRotateTarget} /></td>
                        <td style={{ padding: '12px 14px' }}><StaffPinStatus device={d} now={now} /></td>
                        <td style={{ padding: '12px 14px', fontSize: 13 }}>
                          {d.session ? (
                            <span style={{ color: AMBER, fontWeight: 700 }} data-testid={`kiosk-session-${d.kiosk_id}`}>
                              {fmtMs(d.session.started_at_ms)}
                              <span style={{ color: GREY, fontWeight: 400 }}> · {d.session.mode || 'session'}</span>
                              {d.live && <div style={{ fontSize: 11, color: GREY, fontWeight: 400 }} data-testid={`kiosk-live-${d.kiosk_id}`}>{liveLine(d.live)}</div>}
                            </span>
                          ) : <span style={{ color: '#555' }}>idle</span>}
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: 13, color: d.rows_pending > 0 ? AMBER : GREY }} data-testid={`kiosk-pending-${d.kiosk_id}`}>
                          {d.rows_pending || 0}
                          {d.rows_pending > 0 && d.pending && (
                            <span style={{ fontSize: 11, color: '#555' }}> ({d.pending.sessions || 0}s · {d.pending.collections || 0}c · {d.pending.events || 0}e)</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px' }}><SyncBadge state={d.sync_state} testId={`kiosk-sync-${d.kiosk_id}`} /></td>
                        <td style={{ padding: '12px 14px', fontSize: 13 }} data-testid={`kiosk-commands-pending-${d.kiosk_id}`}
                          title={d.commands_pending > 0 ? 'Commands waiting for the phone (delivered on its next heartbeat)' : 'No commands waiting'}>
                          {d.commands_pending > 0 ? <span style={{ color: AMBER, fontWeight: 700 }}>{d.commands_pending} pending</span> : <span style={{ color: '#555' }}>—</span>}
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: 13, color: d.is_online ? '#fff' : GREY }} title={fmtMs(d.last_seen_ms)}>
                          {fmtAgo(d.last_seen_ms, now)}
                          {d.battery != null && <span style={{ fontSize: 11, color: d.battery <= 15 ? '#FF2D55' : '#555' }}> · {d.battery}%{d.charging ? '⚡' : ''}</span>}
                          <div style={{ fontSize: 11, color: '#555' }}>{d.app_version || '—'}</div>
                        </td>
                        <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                          <div className="flex gap-1.5 justify-end">
                            {d.status === 'active' ? (
                              <>
                                <button onClick={() => setPrompt({ device: d, field: 'name' })} data-testid={`kiosk-rename-${d.kiosk_id}`} title="Rename" style={iconBtn}><Pencil className="h-4 w-4" /></button>
                                <button onClick={() => setPrompt({ device: d, field: 'site_name' })} data-testid={`kiosk-site-${d.kiosk_id}`} title="Set site" style={iconBtn}><MapPin className="h-4 w-4" /></button>
                                <button onClick={() => setRevokeTarget(d)} data-testid={`kiosk-revoke-${d.kiosk_id}`} title="Revoke" style={{ ...iconBtn, color: '#FF2D55' }}><Ban className="h-4 w-4" /></button>
                              </>
                            ) : (
                              <button onClick={() => setRePair(d)} data-testid={`kiosk-repair-${d.kiosk_id}`} title="Pair again"
                                style={{ ...iconBtn, padding: '7px 10px', fontSize: 12, fontWeight: 700, color: GREEN, borderColor: GREEN + '40' }}>
                                <Link2 className="h-4 w-4" /> Pair again
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>

      {pairOpen && <PairDeviceDialog tenants={tenants} onClose={() => setPairOpen(false)} onDone={() => { setPairOpen(false); load(true); }} />}
      {rePair && <PairDeviceDialog replace={rePair} tenants={tenants} onClose={() => setRePair(null)} onDone={() => { setRePair(null); load(true); }} />}
      {prompt && (
        <TextPromptModal
          title={prompt.field === 'name' ? 'Rename device' : 'Set site'}
          label={prompt.field === 'name' ? 'Device name' : 'Site name'}
          initial={prompt.device[prompt.field] || ''}
          required={prompt.field === 'name'}
          testId={`kiosk-prompt-${prompt.field}`}
          onSave={v => saveField(prompt.device, prompt.field, v)}
          onClose={() => setPrompt(null)} />
      )}
      {revokeTarget && (
        <ConfirmModal title={`Revoke ${revokeTarget.name}?`} confirmLabel="Revoke"
          message="The phone keeps working offline with its own ledger, but it stops syncing and shows 'not paired' in Settings. You can pair it again later and its history stays."
          onConfirm={doRevoke} onCancel={() => setRevokeTarget(null)} />
      )}
      {rotateTarget && (
        <ConfirmModal title={`Replace the court link for ${rotateTarget.name}?`} color={AMBER} confirmLabel="Replace link"
          message={`The link is meant to be permanent. Only do this when the court or the owner asked for it: the current link (${rotateTarget.court_link?.code || '—'}) stops opening immediately and the court needs the new one.`}
          onConfirm={doRotate} onCancel={() => setRotateTarget(null)} />
      )}
    </SuperAdminShell>
  );
}
